import { createLogger } from 'shared/services/logger';

// Only log warnings and errors by default. Replace the array with `true` to show all log levels.
const logger = createLogger('UScope:lib:PluginService', ['warn', 'error']);

const generateId = () => {
  return Math.random().toString(36).substr(2, 10);
};

const createContextDictionary = () => {
  const dictionary = {};
  return {
    createDictionary (context) {
      // TODO: don't cause side-effects (e.g. don't mutate the context object).
      context.__CTXID__ = generateId(); // CTXID = context id
      dictionary[context.__CTXID__] = {};
      context.events.on('$destroy', () => delete dictionary[context.__CTXID__]);
    },
    get (name, ...contexts) {
      const context = contexts.find(context => {
        return (context && dictionary[context.__CTXID__]) ? dictionary[context.__CTXID__][name] : false;
      });
      return context ? dictionary[context.__CTXID__][name] : undefined;
    },
    set (name, context, value) {
      dictionary[context.__CTXID__][name] = value;
    }
  };
};

export function PluginService (events) {
  const self = this;

  const pluginLookup = {};

  // Plugins will be instantiated in sequence based on dependencies and order of inclusion
  const startupQueues = {
    uScope: [],
    viewport: []
  };
    // Dictionary to lookup running services.
  const runningServices = createContextDictionary();

  const getDependencies = (plugin) => {
    return plugin.dependencies || (plugin.dependency ? [plugin.dependency] : []);
  };

  const getStartupIndex = (queue, plugin) => {
    let pluginIndex = -1;
    const dependencies = getDependencies(plugin);

    dependencies.forEach((dependency) => {
      const depIndex = queue.indexOf(pluginLookup[dependency]);
      if (depIndex > pluginIndex) {
        pluginIndex = depIndex;
      }
    });

    pluginIndex++;
    return pluginIndex;
  };

  const onUScopeCreated = (uScope) => {
    runningServices.createDictionary(uScope);

    const handleStartupQueue = (queue, uScope, viewport) => {
      const args = [uScope];
      if (viewport) {
        args.push(viewport);
      }
      const getDepInstances = (plugin) => {
        const dependencyList = getDependencies(plugin);
        const dependencies = [];
        const badDeps = [];

        dependencyList.forEach((dependencyName) => {
          const dependency = runningServices.get(dependencyName, uScope, viewport);
          if (!dependency) {
            badDeps.push(dependencyName);
          } else {
            dependencies.push(dependency);
          }
        });

        if (badDeps.length) {
          throw new Error(`Could not locate dependencies: '${badDeps.join(', ')}' for plugin '${plugin.name}'.`);
        }

        return (dependencies.length) ? args.concat(dependencies) : args;
      };

      queue.forEach((plugin) => {
        try {
          if (plugin.hasOwnProperty('canStart') && !plugin.canStart(uScope)) {
            logger.warn(`Not starting ${plugin.name} because it reported it was not allowed to start.`);
            return;
          }
          const dependencies = getDepInstances(plugin);
          let instance;
          if (plugin.factory) {
            logger.log(`Creating ${plugin.name} instance using factory() call`);
            instance = plugin.factory.apply(instance, dependencies);
            if (!instance) {
              throw new Error('factory() method must return an instance.');
            }
          } else {
            logger.log(`Creating ${plugin.name} instance using constructor call`);
            instance = Reflect.construct(plugin.constructor, dependencies);
          }
          instance.configure && instance.configure(uScope.vilSettings.extensions[plugin.name]);
          runningServices.set(plugin.name, (viewport || uScope), instance);
        } catch (e) {
          logger.error(`Exception starting ${plugin.name}.`, e);
        }
      });
    };

    handleStartupQueue(startupQueues.uScope, uScope);

    uScope.events.on('uscope:image:init', (viewport) => {
      runningServices.createDictionary(viewport);

      viewport.events.on('viewport:initialize-layout', () => {
        handleStartupQueue(startupQueues.viewport, uScope, viewport);
      });
    });
  };

  events.on('lbs.uscope:start', onUScopeCreated);

  self.register = (plugin) => {
    if (!plugin.name || !plugin.constructor) {
      throw new Error('Plugins must contain `name` and `constructor` properties.');
    }

    const queue = startupQueues[plugin.extends];

    if (!queue) {
      throw new Error('Plugins must contain an `extends` property. Valid values are `uScope` or `viewport`');
    }

    const startupIndex = getStartupIndex(queue, plugin);

    queue.splice(startupIndex, 0, plugin);

    pluginLookup[plugin.name] = plugin;
  };

  self.getInstance = (context, pluginName) => {
    return runningServices.get(pluginName, context);
  };
}



// WEBPACK FOOTER //
// ./src/uScope/js/lib/services/plugin-service.js